/**
 * Control plans — what is measured, how often, against what limit, and what
 * happens when it is out. Each row that has a TARP escalates through it.
 *
 * Proof-of-concept content. The brake and hydraulic limits are the demo's; the
 * pad-thickness, oil-cleanliness and behaviour limits are plausible site
 * values to be replaced by the customer's own control plan.
 *
 * Data Schema layer — seed data.
 *
 * @see src/versions/v1/schema/operations.ts — `ControlPlanRow`
 * @see ./tarps.ts
 */

import type { ControlPlanRow } from '../../schema/operations.ts';

/** Every control-plan row, class by class. */
export const CONTROL_PLANS: readonly ControlPlanRow[] = [
  {
    class: 'truck',
    component: 'Retarder & service brakes',
    characteristic: 'Rear brake temperature on loaded descent',
    method: 'Live CAN brake_temp_rr_c on connected sites; shift-end daily mean elsewhere',
    specLimit: '< 240 °C (limit), < 220 °C target',
    frequency: 'Continuous (live) · every shift (shift-end)',
    reactionPlan: 'Escalate through TARP-BRK-TEMP — operator, then supervisor brief, then empty-haul restriction',
    tarpId: 'TARP-BRK-TEMP',
  },
  {
    class: 'truck',
    component: 'Retarder & service brakes',
    characteristic: 'Pad thickness remaining',
    method: 'Gauge at PM',
    specLimit: '≥ 25 % remaining',
    frequency: 'PM-500 (PM-250 proposed)',
    reactionPlan: 'Pads changed at the next crib when below 25 %; below 18 % the truck is restricted to empty haul',
    tarpId: 'TARP-BRK-TEMP',
  },
  {
    class: 'truck',
    component: 'Transmission',
    characteristic: 'Brake-throttle overlap and wrong-gear selections',
    method: 'RMS operator-behaviour counters',
    specLimit: '< 3 per shift',
    frequency: 'Weekly review',
    reactionPlan: 'Operating Supervisor coaching; trainer ride-along where the operator runs ≥ 1.8 × site peers',
  },
  {
    class: 'lhd',
    component: 'Hydraulic pump & drive',
    characteristic: 'Hydraulic oil temperature',
    method: 'Shift-end mean',
    specLimit: '< 85 °C',
    frequency: 'Every shift',
    reactionPlan: 'Escalate through TARP-HYD-TEMP',
    tarpId: 'TARP-HYD-TEMP',
  },
  {
    class: 'lhd',
    component: 'Hydraulic pump & drive',
    characteristic: 'Hydraulic oil cleanliness',
    method: 'Oil sample, ISO 4406',
    specLimit: '≤ 18/16/13',
    frequency: 'PM-250',
    reactionPlan: 'Resample; filter change and cooler flush when above',
  },
  {
    class: 'lhd',
    component: 'Bucket & GET',
    characteristic: 'Bucket ramming and boom-overload events',
    method: 'RMS operator-behaviour counters, attributed to the driver on shift',
    specLimit: '< 3 per shift',
    frequency: 'Weekly review',
    reactionPlan: 'Operating Supervisor coaching; trainer ride-along where the operator runs ≥ 1.8 × site peers',
  },
  {
    class: 'jumbo',
    component: 'Rock drill (LH / RH)',
    characteristic: 'Percussion pressure at full collar',
    method: 'Daily mean at full collar',
    specLimit: 'Within 5 % of the start-of-window mean',
    frequency: 'Every shift',
    reactionPlan: 'Escalate through TARP-PERC-PRESS',
    tarpId: 'TARP-PERC-PRESS',
  },
  {
    class: 'longhole',
    component: 'Rock drill',
    characteristic: 'Percussion pressure at full collar',
    method: 'Daily mean at full collar',
    specLimit: 'Within 5 % of the start-of-window mean',
    frequency: 'Every shift',
    reactionPlan: 'Escalate through TARP-PERC-PRESS',
    tarpId: 'TARP-PERC-PRESS',
  },
  {
    class: 'sdrill',
    component: 'DTH hammer & pipes',
    characteristic: 'Penetration rate at constant air',
    method: 'RMS daily mean on an I-MR chart',
    specLimit: 'No SPC run-rule signal',
    frequency: 'Daily',
    reactionPlan: 'Reliability Engineer review on the first signal; hammer exchanged at the pattern change',
  },
  {
    class: 'sdrill',
    component: 'Rotary head',
    characteristic: 'Rotary motor case-drain flow',
    method: 'RMS live, three-minute samples',
    specLimit: '< 1.5 × commissioning baseline',
    frequency: 'Continuous',
    reactionPlan: 'Operator backs off feed force; motor exchange scheduled on the second signal',
  },
  {
    class: 'lhd',
    component: 'Battery pack (LFP, 300 kWh)',
    characteristic: 'Battery warnings and isolation resistance',
    method: 'BMS events',
    specLimit: 'No isolation fault; no derate',
    frequency: 'Continuous',
    reactionPlan: 'Escalate through TARP-BAT-ISO',
    tarpId: 'TARP-BAT-ISO',
  },
  {
    class: 'lhd',
    component: 'Charging interface',
    characteristic: 'Charge handshake success',
    method: 'Charger log',
    specLimit: 'No failed handshakes',
    frequency: 'Every charge',
    reactionPlan: 'Escalate through TARP-CHG-MOIST',
    tarpId: 'TARP-CHG-MOIST',
  },
];
